import s from './FilterItem.module.sass'

import { FC, useRef, useState, useEffect } from 'react'
import Link from '@shared/ui/Navigation/Link'

import { useAppSelector } from '@app/model/store/client'

import { findDataTagForAnalyticBySlug } from '@features/Restaurant/RestaurantFilters/helpers'

import { EGG } from '@shared/api/analytics'
import { Text } from '@shared/ui/Typography/Text'
import { TypeQuickFilter } from '@shared/api/middleware/mappingAPI/city/types'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { useOnScreen } from '@shared/lib/hooks/useOnScreen'

interface Props {
  data: TypeQuickFilter
  citySlug?: string
}

type ImagePair = {
  desktop: string
  mobile: string
}

const FilterItem: FC<Props> = ({ data, citySlug = '' }) => {
  const { isMobile } = useClientResponsive()
  const [mounted, setMounted] = useState(false)

  const storeCityRead = useAppSelector((store) => store.global.cityRead)

  const wrapper = useRef<HTMLAnchorElement>(null)
  const rootForIntersectionObserver = wrapper.current?.parentElement?.parentElement ?? null

  const visible60 = useOnScreen({ ref: wrapper, optionsProps: { threshold: 0.6, root: rootForIntersectionObserver } })
  const visible70 = useOnScreen({ ref: wrapper, optionsProps: { threshold: 0.7, root: rootForIntersectionObserver } })
  const visible80 = useOnScreen({ ref: wrapper, optionsProps: { threshold: 0.8, root: rootForIntersectionObserver } })
  const visible90 = useOnScreen({ ref: wrapper, optionsProps: { threshold: 0.9, root: rootForIntersectionObserver } })

  useEffect(() => setMounted(true), [])

  const getOpacityValue = (): number => {
    const isShowContent = (typeof window !== 'undefined' && window.innerWidth <= 970) || isMobile || visible90

    const opts: [boolean, number][] = [
      [!mounted, 1],
      [isShowContent, 1],
      [visible80, 0.6],
      [visible70, 0.4],
      [visible60, 0.2],
    ]

    return opts.find(([cond]) => cond)?.[1] ?? 0
  }

  const handleClick = () => {
    const dataTag = findDataTagForAnalyticBySlug({ foundSlug: data.slug })
    EGG.common.quick_filter_click({
      filter_apply_type: dataTag.type,
      filter_apply_value: data.slug,
    })
  }

  const image = data.image as ImagePair | null
  const imageSrc = image ? (image[isMobile ? 'mobile' : 'desktop'] ?? '') : ''
  const slug = citySlug || storeCityRead?.slug || ''
  const href = `/${slug}/restaurants/restaurant_list/${data.slug}`

  // картинки с белым фоном
  const isMultiplyBlend = data.slug === 'european_cuisine' || data.slug === 'fish_restaurants' ? true : false

  // с дефисом не переносим
  const isNowrap = data.slug === 'buisness_lunch' ? true : false

  // убираем слово "кухня" для мобилки
  const filteredName = (thisName: string): string => thisName.replace('кухня', '').trim()

  return (
    <Link
      href={href}
      onClick={handleClick}
      className={s.filter}
      key={data.slug}
      ref={wrapper}
      title={data.name}
      scroll={false}>
      <div className={s.imageWrapper}>
        <img
          src={imageSrc}
          alt={data.name}
          className={s.imageElem}
          data-blend={isMultiplyBlend ? 'multiply' : undefined}
          style={{
            opacity: getOpacityValue(),
            width: isMobile ? '100px' : '130px',
            height: isMobile ? '100px' : '130px',
            objectFit: 'cover',
          }}
        />

        {isMobile && (
          <Text
            sizes={'S'}
            className={s.title}
            tag={'h4'}
            style={{ opacity: getOpacityValue() }}
            data-nowrap={isNowrap ? 'nowrap' : undefined}>
            {filteredName(data.name)}
          </Text>
        )}
      </div>

      {!isMobile && (
        <Text sizes={'S'} className={s.title} tag={'h4'} style={{ opacity: getOpacityValue() }}>
          {data.name}
        </Text>
      )}
    </Link>
  )
}

export default FilterItem
